import { getLocaleFromCountryCode } from '@src/i18n/locale.util'
import type { CountryCode } from '@src/i18n/locales'
import type useAddressSearch from '@src/utils/address-search.util'
import type { Geolocation } from '@src/utils/geolocation'
import { getLocation } from '@src/utils/geolocation'

export interface SSEAddress {
  address1: string
  address2: string
  city: string
  state: string
  postalCode: string
  country: CountryCode
}

/**
 * Factory function to create a searchAddress function
 * to use the Experian API to search for addresses
 * and return the results in the SSE format.
 */
export function searchAddressFactory({
  addressHelpers,
  geolocation,
}: {
  addressHelpers: ReturnType<typeof useAddressSearch>
  geolocation: Geolocation | null
}) {
  return async function searchAddress(address: SSEAddress): Promise<SSEAddress[]> {
    const locale = getLocaleFromCountryCode(address.country.toUpperCase())

    // @example '123 Main St, Apt 2, Springfield, IL, 62701'
    const searchText = [
      address.address1,
      address.address2,
      address.city,
      address.state,
      address.postalCode,
    ]
      .filter(Boolean)
      .join(', ')

    const suggestions = await addressHelpers.searchAddress({
      searchText,
      location: getLocation(geolocation),
      countryIso: locale.countryIsoCode,
      addressDataset: locale.addressDataset,
      options: [
        { name: 'search_type', value: 'singleline' },
        { name: 'prompt_set', value: 'optimal' },
      ],
    })

    const experianAddresses = (
      await Promise.all(
        (suggestions ?? []).map(async (suggestion) =>
          addressHelpers.getAddressByGlobalAddressKey({
            globalAddressKey: suggestion.globalAddressKey,
          })
        )
      )
    ).filter((a) => a !== null)

    const sseAddresses = experianAddresses.map((globalAddress) => {
      return {
        address1: globalAddress.address.addressLine1,
        address2: globalAddress.address.addressLine2,
        city: globalAddress.address.locality,
        state: globalAddress.address.region,
        postalCode: globalAddress.address.postalCode,
        country: locale.countryCode,
      } as SSEAddress
    })

    return sseAddresses
  }
}
